import { Workstation } from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import { EmptyStates } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { DropdownField, SearchField, useDebouncedValue, useFormField } from '@frontend/design-system';
import { ReducedOperatory } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/useReviewRequest';

type WorkstationDropdownSearchListProps = {
  workstationList: Workstation[] | ReducedOperatory[];
  fieldProps: ReturnType<typeof DropdownField>['props'];
  disabled: boolean;
};

export const WorkstationDropdownSearchList = ({
  workstationList,
  fieldProps,
  disabled,
}: WorkstationDropdownSearchListProps) => {
  const { t } = useTranslation('scheduleCalendarRequest');
  const searchFieldProps = useFormField({ type: 'text' });
  const searchText = useDebouncedValue(searchFieldProps.value);

  const searchFilteredList = workstationList.filter(({ workstationName }) =>
    workstationName?.toLowerCase().includes(searchText.toLowerCase())
  );

  // Always include the currently selected workstation in the filtered workstationList to prevent losing the display value when searching
  const selectedWorkstation = workstationList.find(({ id }) => id === fieldProps.value);
  const filteredList =
    selectedWorkstation && !searchFilteredList.find(({ id }) => id === selectedWorkstation.id)
      ? [selectedWorkstation, ...searchFilteredList]
      : searchFilteredList;

  return (
    <DropdownField disabled={disabled} label={t('Workstation')} {...fieldProps}>
      <SearchField name='search-workstation' {...searchFieldProps} css={{ margin: theme.spacing(1, 2) }} />
      {!filteredList?.length ? (
        <EmptyStates
          emptyStateConfig={{
            type: 'schedule',
            header: t('No matching workstation names found'),
            description: '',
          }}
        />
      ) : (
        filteredList.map(({ id, workstationName }) => (
          <DropdownField.Option key={id} value={id ?? ''}>
            {workstationName}
          </DropdownField.Option>
        ))
      )}
    </DropdownField>
  );
};

type WorkstationDropdownSearchListV2Props = {
  workstationList: { realTimeAvailable: Workstation[]; standard: Workstation[] };
  showGroupedWorkstations: boolean;
  isLoadingRealTimeWorkstations: boolean;
  fieldProps: ReturnType<typeof DropdownField>['props'];
  disabled: boolean;
};

export const WorkstationDropdownSearchListV2 = ({
  workstationList,
  fieldProps,
  showGroupedWorkstations,
  isLoadingRealTimeWorkstations,
  disabled,
}: WorkstationDropdownSearchListV2Props) => {
  const { t } = useTranslation('schedule');

  const workstationSearchFieldProps = useFormField({ type: 'text' });
  const searchText = useDebouncedValue(workstationSearchFieldProps.value);

  const selectedWorkstationValue = fieldProps.value;
  const selectedWorkstation =
    workstationList.realTimeAvailable.find(({ id }) => id === selectedWorkstationValue) ||
    workstationList.standard.find(({ id }) => id === selectedWorkstationValue);

  const baseFilteredStandardList = searchText
    ? workstationList.standard.filter(({ workstationName }) =>
        workstationName?.toLowerCase().includes(searchText.toLowerCase())
      )
    : workstationList.standard;

  const baseFilteredRealTimeList = searchText
    ? workstationList.realTimeAvailable.filter(({ workstationName }) =>
        workstationName?.toLowerCase().includes(searchText.toLowerCase())
      )
    : workstationList.realTimeAvailable;

  const filteredStandardWorkstationList =
    selectedWorkstation &&
    workstationList.standard.find(({ id }) => id === selectedWorkstation.id) &&
    !baseFilteredStandardList.find(({ id }) => id === selectedWorkstation.id)
      ? [selectedWorkstation, ...baseFilteredStandardList]
      : selectedWorkstation && workstationList.standard.find(({ id }) => id === selectedWorkstation.id)
      ? [selectedWorkstation, ...baseFilteredStandardList.filter(({ id }) => id !== selectedWorkstation.id)]
      : baseFilteredStandardList;

  const filteredRealTimeWorkstationList =
    selectedWorkstation &&
    workstationList.realTimeAvailable.find(({ id }) => id === selectedWorkstation.id) &&
    !baseFilteredRealTimeList.find(({ id }) => id === selectedWorkstation.id)
      ? [selectedWorkstation, ...baseFilteredRealTimeList]
      : selectedWorkstation && workstationList.realTimeAvailable.find(({ id }) => id === selectedWorkstation.id)
      ? [selectedWorkstation, ...baseFilteredRealTimeList.filter(({ id }) => id !== selectedWorkstation.id)]
      : baseFilteredRealTimeList;

  const filteredWorkstationList = {
    standard: filteredStandardWorkstationList,
    realTimeAvailable: filteredRealTimeWorkstationList,
  };

  return (
    <DropdownField disabled={disabled} label={t('Workstation')} {...fieldProps}>
      <SearchField
        name='search-Workstation'
        {...workstationSearchFieldProps}
        css={{
          margin: theme.spacing(1, 2),
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: theme.colors.white,
        }}
      />
      {searchText &&
      filteredWorkstationList.standard.length === 0 &&
      filteredWorkstationList.realTimeAvailable.length === 0 ? (
        <EmptyStates
          emptyStateConfig={{
            type: 'schedule',
            header: t('No matching workstation names found'),
            description: '',
          }}
        />
      ) : showGroupedWorkstations ? (
        <DropdownField.OptionGroup label={t('Available')}>
          {filteredWorkstationList.realTimeAvailable.length > 0 ? (
            filteredWorkstationList.realTimeAvailable.map(({ id, workstationName }) => (
              <DropdownField.Option value={id ?? ''} key={id}>
                {workstationName}
              </DropdownField.Option>
            ))
          ) : (
            <DropdownField.Option disabled value='no-available-workstation' key='no-available-workstation'>
              {isLoadingRealTimeWorkstations ? t('Loading...') : t('There are no available workstations')}
            </DropdownField.Option>
          )}
        </DropdownField.OptionGroup>
      ) : null}
      {showGroupedWorkstations ? (
        <DropdownField.OptionGroup label={t('Others')}>
          {filteredWorkstationList.standard.map(({ id, workstationName }) => (
            <DropdownField.Option value={id ?? ''} key={id}>
              {workstationName}
            </DropdownField.Option>
          ))}
        </DropdownField.OptionGroup>
      ) : (
        filteredWorkstationList.standard.map(({ id, workstationName }) => (
          <DropdownField.Option value={id ?? ''} key={id}>
            {workstationName}
          </DropdownField.Option>
        ))
      )}
    </DropdownField>
  );
};
